import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import moment from "moment";
import SurveyForm from "./SurveyForm";
import SurveyDone from "./SurveyDone";
import {
  getClinicSurvey,
  getPublicSurvey,
  submitClinicComplaint,
  submitClinicSurvey,
  submitPublicComplaint,
  submitPublicSurvey,
} from "../../api/feedback";
import { COMPLAINT_CATEGORIES } from "./feedbackUtils";
import "./FeedbackScreen.css";

/**
 * Logged-out survey page — the link a patient receives by SMS / email / in-app
 * (`/survey?id=<token>`) or scans from the clinic's waiting-room QR
 * (`/survey?clinic=<clinicId>`). NABH AAC.6.a Steps 16–17.
 *
 * No login: the token IS the authorisation (single-use, 30 days). A logged-in
 * patient sees the same invitation under Feedback → Surveys as well.
 */
const PublicSurveyScreen = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("id") || "";
  const clinicId = searchParams.get("clinic") || "";
  const mode = token ? "token" : clinicId ? "clinic" : "none";

  const [loading, setLoading] = useState(mode !== "none");
  const [loadError, setLoadError] = useState(
    mode === "none" ? "This link is invalid or has expired." : ""
  );
  const [form, setForm] = useState(null);
  const [result, setResult] = useState(null);
  const [showComplaint, setShowComplaint] = useState(false);
  const [complaintDone, setComplaintDone] = useState(null);

  useEffect(() => {
    if (mode === "none") return undefined;
    let alive = true;
    (async () => {
      try {
        const data =
          mode === "token"
            ? await getPublicSurvey(token)
            : await getClinicSurvey(clinicId);
        if (alive) setForm(data);
      } catch (err) {
        if (alive) setLoadError(err?.message || "This link is invalid or has expired.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [mode, token, clinicId]);

  const submitSurvey = (payload) =>
    mode === "token"
      ? submitPublicSurvey(token, { ...payload, channel: "link" })
      : submitClinicSurvey(clinicId, payload);

  const submitComplaint = (payload) =>
    mode === "token"
      ? submitPublicComplaint(token, payload)
      : submitClinicComplaint(clinicId, payload);

  const goHome = () => navigate("/", { replace: true });

  let body;
  if (loading) {
    body = (
      <div className="fb_center">
        <CircularProgress size="1.6rem" />
      </div>
    );
  } else if (loadError) {
    body = (
      <div className="fb_card">
        <p className="fb_error" role="alert">
          {loadError}
        </p>
        <p className="fb_muted">
          If you have a MyHealthVault account, your surveys are also under
          Feedback &amp; complaints after you log in.
        </p>
        <button type="button" className="fb_btn_primary" onClick={() => navigate("/login")}>
          Log in
        </button>
      </div>
    );
  } else if (complaintDone) {
    body = (
      <div className="fb_card fb_done">
        <div className="fb_done_icon" aria-hidden="true">
          ✓
        </div>
        <h2 className="fb_survey_title">Complaint received</h2>
        <p className="fb_muted">{complaintDone.message}</p>
        <p className="fb_reference" data-testid="complaint-reference">
          Reference number: <strong>{complaintDone.referenceId}</strong>
        </p>
        <p className="fb_muted">
          Keep this number. The clinic will update you as they work on it.
        </p>
        <button type="button" className="fb_btn_primary" onClick={goHome}>
          Done
        </button>
      </div>
    );
  } else if (result) {
    body = <SurveyDone result={result} onDone={goHome} doneLabel="Done" />;
  } else if (form?.alreadySubmitted) {
    body = (
      <div className="fb_card">
        <h2 className="fb_survey_title">Already submitted</h2>
        <p className="fb_muted">
          Thank you — this survey has already been answered. Each link can be used once.
        </p>
        <button type="button" className="fb_btn_primary" onClick={goHome}>
          Done
        </button>
      </div>
    );
  } else if (showComplaint) {
    body = (
      <PublicComplaintForm
        withContact={mode === "clinic"}
        submit={submitComplaint}
        onCreated={setComplaintDone}
        onCancel={() => setShowComplaint(false)}
      />
    );
  } else {
    const greeting = form?.patientFirstName ? `Hi ${form.patientFirstName}, ` : "";
    body = (
      <>
        <p className="fb_intro">
          {greeting}how was your visit
          {form?.clinicName ? ` to ${form.clinicName}` : ""}
          {form?.visitDate ? ` on ${moment(form.visitDate).format("DD MMM YYYY")}` : ""}?
          Your answers go only to the clinic.
        </p>
        <SurveyForm
          target={{
            kind: "public",
            clinicName: form?.clinicName,
            doctorName: form?.doctorName,
            date: form?.visitDate,
          }}
          survey={{ surveyName: form?.surveyName, questions: form?.questions }}
          submit={submitSurvey}
          onSubmitted={setResult}
          onCancel={goHome}
        />
        <p className="fb_muted fb_center_text">
          Something went wrong during the visit?{" "}
          <button type="button" className="fb_link_btn" onClick={() => setShowComplaint(true)}>
            Raise a complaint instead
          </button>
        </p>
      </>
    );
  }

  return (
    <div className="fb_layout" id="layout">
      <div className="fb_header">
        <span className="fb_title">Patient feedback</span>
      </div>
      <div className="fb_body">{body}</div>
    </div>
  );
};

/** Compact complaint form for the logged-out page (no clinic picker — the link fixes it). */
const PublicComplaintForm = ({ withContact, submit, onCreated, onCancel }) => {
  const [category, setCategory] = useState("care");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [relation, setRelation] = useState("self");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    const s = subject.trim();
    const d = description.trim();
    if (s.length < 3) return setError("Subject must be at least 3 characters");
    if (d.length < 10) return setError("Please describe the issue in at least 10 characters");
    setError("");
    setSubmitting(true);
    try {
      const payload = { category, subject: s, description: d, relation };
      if (withContact) {
        if (name.trim()) payload.name = name.trim();
        if (phone.trim()) payload.phone = phone.trim();
      }
      onCreated(await submit(payload));
    } catch (err) {
      setError(err?.message || "Could not register your complaint");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form className="fb_card" onSubmit={onSubmit} noValidate>
      <h2 className="fb_survey_title">Raise a complaint</h2>
      <p className="fb_muted">
        Tell the clinic what went wrong. You will get a reference number and updates as they work on it.
      </p>
      {withContact ? (
        <>
          <label className="fb_label" htmlFor="pc_name">Your name (optional)</label>
          <input id="pc_name" className="fb_input" value={name} onChange={(e) => setName(e.target.value)} />
          <label className="fb_label" htmlFor="pc_phone">Mobile number (optional)</label>
          <input id="pc_phone" className="fb_input" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
        </>
      ) : null}
      <label className="fb_label" htmlFor="pc_category">Category</label>
      <select id="pc_category" className="fb_select" value={category} onChange={(e) => setCategory(e.target.value)}>
        {COMPLAINT_CATEGORIES.map((c) => (
          <option key={c.value} value={c.value}>{c.label}</option>
        ))}
      </select>
      <label className="fb_label" htmlFor="pc_subject">Subject</label>
      <input id="pc_subject" className="fb_input" maxLength={200} value={subject} onChange={(e) => setSubject(e.target.value)} />
      <label className="fb_label" htmlFor="pc_description">Description</label>
      <textarea id="pc_description" className="fb_textarea" rows={5} maxLength={5000} value={description} onChange={(e) => setDescription(e.target.value)} />
      <fieldset className="fb_fieldset">
        <legend className="fb_label">This is for</legend>
        <label className="fb_radio">
          <input type="radio" name="pc_relation" checked={relation === "self"} onChange={() => setRelation("self")} /> Myself
        </label>
        <label className="fb_radio">
          <input type="radio" name="pc_relation" checked={relation === "family"} onChange={() => setRelation("family")} /> A family member
        </label>
      </fieldset>
      {error ? (
        <p className="fb_error" role="alert">{error}</p>
      ) : null}
      <div className="fb_actions">
        <button type="button" className="fb_btn_secondary" onClick={onCancel} disabled={submitting}>Cancel</button>
        <button type="submit" className="fb_btn_primary" disabled={submitting}>
          {submitting ? <CircularProgress sx={{ color: "white" }} size="1rem" /> : "Submit complaint"}
        </button>
      </div>
    </form>
  );
};

export default PublicSurveyScreen;
